// Kekes are solid (the road is not a ghost town): two that touch are pushed apart, and two that
// meet fast enough crash. A crash stops both dead, leaves them shaken (slow) for a moment and
// shakes a passenger out of each. Anyone can crash into anyone, agberos included, so a crash says
// nothing about sides. Spirits and kekes in a shortcut pass through. Pure and deterministic.
import { getMap, TILE } from '@wahala/maps';
import { locked } from './arrest.js';
import { PLAYER_RADIUS, slide } from './geometry.js';
import { shakeOut } from './rides.js';
import { shadowed } from './roles.js';
import { rulesFor } from './rules.js';
/** Closing speed (world units per tick, full speed is 22) at which a touch becomes a crash. */
export const CRASH_SPEED = 14;
/** Shaken after a crash: this long, at this share of the speed. */
export const SHAKEN_MS = 1_500;
export const SHAKEN_FACTOR = 0.5;
/** How long a crash stays on the road for the phones that can see it. */
export const CRASH_SHOWN_MS = 2_000;
/** Two kekes touch when their centres are closer than this. */
const TOUCH = 2 * PLAYER_RADIUS;
export function createCrashes() {
    return { nextId: 1, shakenUntil: {}, recent: [] };
}
export const shaken = (s, id) => (s.crashes?.shakenUntil[id] ?? 0) > s.now;
/**
 * On the road and solid: alive, still here, not in a shortcut, not a shadow out of sight, not locked
 * in an arrest or the yard (and the mode agrees).
 */
const solid = (s, p) => p.alive &&
    !p.departed &&
    p.vent === null &&
    !locked(s, p.id) &&
    !shadowed(s, p) &&
    rulesFor(s).solid?.(s, p) !== false;
/** After the kekes move each tick: push touching pairs apart, and crash the fast ones. */
export function collideKekes(s, events) {
    const c = s.crashes;
    if (!c)
        return;
    c.recent = c.recent.filter((x) => s.now - x.at < CRASH_SHOWN_MS);
    const map = getMap(s.mapId);
    const on = s.order.map((id) => s.players[id]).filter((p) => solid(s, p));
    const passes = rulesFor(s).passes;
    for (let i = 0; i < on.length; i++) {
        for (let j = i + 1; j < on.length; j++) {
            const a = on[i];
            const b = on[j];
            const dx = b.pos.x - a.pos.x;
            const dy = b.pos.y - a.pos.y;
            const d2 = dx * dx + dy * dy;
            if (d2 >= TOUCH * TOUCH || passes?.(s, a, b))
                continue;
            const d = Math.sqrt(d2);
            // the line between them (exactly on top of each other: the lower seat goes left)
            const nx = d === 0 ? 1 : dx / d;
            const ny = d === 0 ? 0 : dy / d;
            // how fast they were closing along that line
            const closing = (a.vel.x - b.vel.x) * nx + (a.vel.y - b.vel.y) * ny;
            const half = Math.ceil((TOUCH - d) / 2);
            a.pos = slide(map, a.pos, -Math.round(nx * half), -Math.round(ny * half));
            b.pos = slide(map, b.pos, Math.round(nx * half), Math.round(ny * half));
            if (closing < CRASH_SPEED || shaken(s, a.id) || shaken(s, b.id)) {
                // a nudge: each loses only the speed it was pushing into the other with
                a.vel = shed(a.vel, nx, ny, 1);
                b.vel = shed(b.vel, nx, ny, -1);
                continue;
            }
            crash(s, a, b, events);
        }
    }
}
/** Take away the part of a velocity heading along (nx, ny) times dir; the rest (sliding past) stays. */
function shed(v, nx, ny, dir) {
    const along = (v.x * nx + v.y * ny) * dir;
    if (along <= 0)
        return v;
    return { x: v.x - Math.round(nx * along * dir), y: v.y - Math.round(ny * along * dir) };
}
function crash(s, a, b, events) {
    const c = s.crashes;
    const pos = { x: Math.round((a.pos.x + b.pos.x) / 2), y: Math.round((a.pos.y + b.pos.y) / 2) };
    for (const p of [a, b]) {
        p.vel = { x: 0, y: 0 };
        c.shakenUntil[p.id] = s.now + SHAKEN_MS;
        shakeOut(s, p.id, events);
    }
    c.recent.push({ id: `x${c.nextId++}`, pos, at: s.now });
    events.push({ t: 'road.crash', a: a.id, b: b.id, pos: { ...pos } });
}
/** A meeting clears the road: nobody stays shaken, no crash is left on show. */
export function clearCrashes(s) {
    if (!s.crashes)
        return;
    s.crashes.shakenUntil = {};
    s.crashes.recent = [];
}
/** How far a knock shoves the keke it hits. */
export const KNOCK_SHOVE = Math.round(0.6 * TILE);
//# sourceMappingURL=crashes.js.map